import { Cancel01Icon } from '@hugeicons/core-free-icons';
import {
  useGetCommunityBySlug,
  useGetEvent,
  useGetSession,
  useGetSessionAttendees,
  useGetSessionLocation,
  useGetSessionsByEventId,
  useSortedSpeakersByFirstNameFromCache,
} from '@sunrise-ui/api-client';
import { SessionStatus } from '@sunrise-ui/api/events/api';
import type { WithResponsive } from '@sunrise-ui/primitives';
import {
  Button,
  ButtonVariants,
  COMMUNITY_APP_URL,
  Colors,
  ExpandableText,
  Header,
  HeaderButton,
  Loader,
  LogoIcon,
  Modal,
  TextBold,
  formatLongDateWithYear,
  formatTimeRange,
  useDynamicStyles,
  useMediaQueries,
} from '@sunrise-ui/primitives';
import { router, useFocusEffect, usePathname } from 'expo-router';
import React, { useCallback, useState } from 'react';
import {
  Linking,
  Platform,
  ScrollView,
  StyleSheet,
  TouchableOpacity,
  View,
} from 'react-native';

import { CommunityResult } from '../communities/CommunityResult';
import { ProfileBio } from '../profiles/ProfileBio';
import { ProfileResult } from '../profiles/ProfileResult';
import { DesktopModalHeader } from './DesktopModalHeader';
import { GettingThereModal } from './GettingThereModal';
import { SessionTicketCTA } from './SessionTicketCTA';
import { SessionTimeLocationActions } from './SessionTimeLocationActions';
import { CacheAwareSessionCardTags } from './cache/CacheAwareSessionCardTags';

type Props = {
  isManagerView?: boolean;
  sessionId?: string;
  closeModal: () => void;
};

export function ConfirmedSessionDetailsModal({
  isManagerView = false,
  sessionId,
  closeModal,
}: Props) {
  const styles = useDynamicStyles(createStyles);
  const { isMobile } = useMediaQueries();
  const pathname = usePathname();
  const [isGettingThereModalVisible, setIsGettingThereModalVisible] =
    useState(false);

  const { data: session, isLoading: isLoadingSession } =
    useGetSession(sessionId);
  const eventId = session?.event_id;
  const { data: speakers } = useGetSessionAttendees(sessionId, true);

  // Get session location
  const sessionLocationId = session?.location_id;
  const { data: sessionLocation, isLoading: isLoadingSessionLocation } =
    useGetSessionLocation(sessionLocationId);
  const { name: sessionLocationName } = sessionLocation ?? {};

  // Sort speakers alphabetically by first name using custom hook
  const { sortedSpeakers } = useSortedSpeakersByFirstNameFromCache(speakers);

  // Fetch event and sessions to determine single-session event and community id
  const { data: event } = useGetEvent(eventId);
  const { data: sessionsForEvent } = useGetSessionsByEventId(eventId, false);
  const isSingleSessionEvent = Boolean((sessionsForEvent ?? []).length === 1);
  const communityId = event?.community_id;

  // Extract community slug from current pathname to determine page community context
  // Routes: /community/[communitySlug] (auth) or /communities/[communitySlug] (nonAuth)
  const communitySlugMatch = pathname.match(/\/communit(?:y|ies)\/([^/]+)/);
  const currentCommunitySlug = communitySlugMatch?.[1];

  // Get the current page community data if we're on a community page
  const { data: currentPageCommunity } =
    useGetCommunityBySlug(currentCommunitySlug);

  const hideSessionModal = () => {
    closeModal();
  };

  useFocusEffect(
    useCallback(() => {
      return () => {
        hideSessionModal();
      };
    }, []),
  );

  const header = isMobile ? (
    <Header
      headerRight={
        <HeaderButton icon={Cancel01Icon} onPress={hideSessionModal} />
      }
      mobileLogo={LogoIcon}
    />
  ) : (
    <DesktopModalHeader
      title={session?.topic_name}
      onClose={hideSessionModal}
    />
  );

  // Build session time and date display separately
  const timeRange = formatTimeRange(session?.start_date, session?.end_date);
  const formattedSessionDate = formatLongDateWithYear(session?.start_date);

  // Only show modal for confirmed or scheduled sessions
  if (session?.status !== SessionStatus.Confirmed && !isLoadingSession) {
    return null;
  }

  return (
    <Modal
      visible={!!sessionId}
      hide={hideSessionModal}
      header={header}
      contentStyle={styles.modalContent}
    >
      <View style={styles.container}>
        {isLoadingSession ? (
          <Loader style={styles.loader} />
        ) : (
          <ScrollView contentContainerStyle={styles.scrollContent}>
            <View style={styles.sessionInfo}>
              {isMobile && (
                <TextBold style={styles.title} numberOfLines={2}>
                  {session?.topic_name}
                </TextBold>
              )}
              <SessionTimeLocationActions
                formattedDate={formattedSessionDate}
                formattedTime={timeRange}
                locationName={sessionLocationName}
                isLoadingLocation={isLoadingSessionLocation}
                actions={
                  !isManagerView ? (
                    <SessionTicketCTA
                      eventId={eventId ?? ''}
                      sessionId={sessionId ?? ''}
                      pageCommunityId={currentPageCommunity?.id}
                      isSingleSessionEvent={isSingleSessionEvent}
                    />
                  ) : undefined
                }
              />
              <View style={styles.tagRow}>
                <View style={styles.tagsContainer}>
                  <CacheAwareSessionCardTags
                    sessionId={sessionId ?? ''}
                    eventId={eventId ?? ''}
                    showAllTags={true}
                  />
                </View>
              </View>
              <ExpandableText
                text={session?.topic_description}
                charLimit={350}
              />
            </View>
            {isSingleSessionEvent && communityId ? (
              <CommunityResult
                communityId={communityId}
                size="small"
                shouldNavigateOnClick={true}
                isOneLineText={true}
                onPress={hideSessionModal}
              />
            ) : null}
            {sortedSpeakers?.map((speaker) => {
              const handleProfilePress = () => {
                if (speaker.user_id) {
                  if (isManagerView) {
                    // In manager app, open community app in new tab
                    const url = `${COMMUNITY_APP_URL}/to/${speaker.user_id}`;
                    if (Platform.OS === 'web') {
                      window.open(url, '_blank');
                    } else {
                      void Linking.openURL(url);
                    }
                  } else {
                    // Regular navigation within the same app
                    router.navigate(`/to/${speaker.user_id}`);
                    hideSessionModal();
                  }
                }
              };

              return (
                <TouchableOpacity
                  key={speaker.user_id}
                  style={styles.speakerInfo}
                  onPress={handleProfilePress}
                >
                  <ProfileResult userId={speaker.user_id} size="small" />
                  <ProfileBio userId={speaker.user_id} />
                </TouchableOpacity>
              );
            })}
          </ScrollView>
        )}

        {/* Getting There button (show for scheduled/confirmed sessions with location) */}
        {!isManagerView &&
          !isLoadingSession &&
          session?.location_id &&
          (session?.status === SessionStatus.Scheduled ||
            session?.status === SessionStatus.Confirmed) && (
            <View style={styles.gettingThereButtonContainer}>
              <Button
                variant={ButtonVariants.PINK}
                size="medium"
                onPress={() => setIsGettingThereModalVisible(true)}
              >
                Getting There
              </Button>
            </View>
          )}
      </View>

      {/* Getting There Modal */}
      <GettingThereModal
        locationId={session?.location_id}
        visible={isGettingThereModalVisible}
        onClose={() => setIsGettingThereModalVisible(false)}
      />
    </Modal>
  );
}

const createStyles = ({ isMobile }: WithResponsive) =>
  StyleSheet.create({
    container: {
      flex: 1,
      width: '100%',
      justifyContent: 'flex-end',
    },
    scrollContent: {
      gap: 24,
      marginHorizontal: isMobile ? 16 : 32,
    },
    sessionInfo: {
      gap: 12,
    },
    modalContent: {
      padding: 0,
      paddingTop: isMobile ? 8 : 24,
      paddingBottom: isMobile ? 24 : 32,
    },
    tagRow: {
      flexDirection: 'row',
      flexWrap: 'nowrap',
      justifyContent: 'flex-start',
      alignItems: 'center',
      gap: 8,
      marginTop: 12,
    },
    tagsContainer: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 8,
      flex: 1,
      minWidth: 0,
    },
    actionButtons: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
      marginLeft: 'auto',
      flexShrink: 0,
    },
    title: {
      fontSize: 20,
      flex: 1,
    },
    speakerInfo: {
      gap: 12,
    },
    emailsContainer: {
      gap: 4,
    },
    loader: {
      position: 'absolute',
      top: 0,
      left: 0,
      bottom: 0,
      right: 0,
      backgroundColor: Colors.purple0,
    },
    approvalButtonsContainerDesktop: {
      position: 'absolute',
      bottom: 24,
      right: 32,
      zIndex: 10,
    },
    approvalButtonsContainerMobile: {
      width: '100%',
      alignItems: 'center',
      justifyContent: 'center',
      paddingTop: 16,
      marginTop: 16,
      borderTopWidth: 1,
      borderTopColor: Colors.purple1,
    },
    gettingThereButtonContainer: {
      width: '100%',
      alignItems: 'center',
      justifyContent: 'center',
      paddingTop: 16,
      marginTop: 16,
    },
  });
